import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Loader2, Truck, PackageCheck, Search, CheckCircle, Lock, FileText, Archive, Printer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RmaStatusBadge } from './RmaStatusBadge';
import { useUpdateRmaStatus, useUpdateRma, useDeleteRma } from '../hooks/useRma';
import type { RmaOrder, RmaResolution, RmaStatus } from '../types';
import { RMA_REASON_LABELS } from '../types';

interface RmaDetailHeaderProps {
  rma: RmaOrder;
}

export function RmaDetailHeader({ rma }: RmaDetailHeaderProps) {
  const navigate = useNavigate();
  const { t } = useTranslation('operations');
  const updateStatus = useUpdateRmaStatus();
  const updateRma = useUpdateRma();
  const deleteRma = useDeleteRma();
  const [resolution, setResolution] = useState<RmaResolution | ''>('');

  const isLoading = updateStatus.isPending || updateRma.isPending || deleteRma.isPending;

  const handleDelete = () => {
    deleteRma.mutate(rma.id, {
      onSuccess: () => navigate('/operations/rma'),
    });
  };

  const handleStatusTransition = (nextStatus: RmaStatus) => {
    updateStatus.mutate({ id: rma.id, status: nextStatus });
  };

  const handleResolve = () => {
    if (!resolution) return;
    updateRma.mutate(
      { id: rma.id, data: { resolution } },
      {
        onSuccess: () => {
          updateStatus.mutate({ id: rma.id, status: 'resolved' });
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      {/* Top row: RMA number, status, actions */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold font-mono">{rma.rmaNumber}</h1>
            <RmaStatusBadge status={rma.status} />
          </div>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {rma.accountName && <span>{rma.accountName}</span>}
            {rma.quoteNumber && (
              <span className="flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                {rma.quoteNumber}
              </span>
            )}
            <span>{t(`rma.reason.${rma.reason}`, RMA_REASON_LABELS[rma.reason] || rma.reason)}</span>
            {rma.reasonNotes && (
              <span className="text-xs italic truncate max-w-xs">{rma.reasonNotes}</span>
            )}
          </div>
        </div>

        {/* Action buttons based on status */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" title={t('rma.detail.print', 'Print RMA')} onClick={() => window.print()}>
            <Printer className="h-4 w-4" />
          </Button>
          {rma.status === 'label_generated' && (
            <Button onClick={() => handleStatusTransition('shipped')} disabled={isLoading}>
              {updateStatus.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Truck className="h-4 w-4 mr-1" />
              )}
              {t('rma.detail.markShipped', 'Mark as Shipped')}
            </Button>
          )}

          {rma.status === 'shipped' && (
            <Button onClick={() => handleStatusTransition('received')} disabled={isLoading}>
              {updateStatus.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <PackageCheck className="h-4 w-4 mr-1" />
              )}
              {t('rma.detail.markReceived', 'Mark as Received')}
            </Button>
          )}

          {rma.status === 'received' && (
            <Button onClick={() => handleStatusTransition('inspecting')} disabled={isLoading}>
              {updateStatus.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Search className="h-4 w-4 mr-1" />
              )}
              {t('rma.detail.beginInspection', 'Begin Inspection')}
            </Button>
          )}

          {rma.status === 'inspecting' && (
            <div className="flex items-center gap-2">
              <Select value={resolution} onValueChange={(v) => setResolution(v as RmaResolution)}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t('rma.detail.resolutionPlaceholder', 'Resolution...')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="repaired">{t('rma.resolution.repaired', 'Repaired')}</SelectItem>
                  <SelectItem value="replaced">{t('rma.resolution.replaced', 'Replaced')}</SelectItem>
                  <SelectItem value="scrapped">{t('rma.resolution.scrapped', 'Scrapped')}</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleResolve} disabled={isLoading || !resolution}>
                {updateStatus.isPending ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-1" />
                )}
                {t('rma.detail.markResolved', 'Mark as Resolved')}
              </Button>
            </div>
          )}

          {rma.status === 'resolved' && (
            <Button onClick={() => handleStatusTransition('closed')} disabled={isLoading}>
              {updateStatus.isPending ? (
                <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              ) : (
                <Lock className="h-4 w-4 mr-1" />
              )}
              {t('rma.detail.close', 'Close RMA')}
            </Button>
          )}

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="icon" disabled={isLoading} title={t('rma.detail.archiveTitle', 'Archive RMA')}>
                {deleteRma.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Archive className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('rma.detail.archiveTitle', 'Archive RMA')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    'rma.detail.archiveConfirm',
                    'Archive {{number}}? It will be removed from the active list but can be retrieved later.',
                    { number: rma.rmaNumber },
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('rma.detail.cancel', 'Cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  {t('rma.detail.archiveAction', 'Archive')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Timeline dates */}
      <div className="flex items-center gap-6 text-xs text-muted-foreground">
        <div>
          <span className="font-medium">{t('rma.detail.createdLabel', 'Created: ')}</span>
          {format(new Date(rma.createdAt), 'MMM d, yyyy h:mm a')}
        </div>
        {rma.shippedAt && (
          <div>
            <span className="font-medium">{t('rma.detail.shippedLabel', 'Shipped: ')}</span>
            {format(new Date(rma.shippedAt), 'MMM d, yyyy h:mm a')}
          </div>
        )}
        {rma.receivedAt && (
          <div>
            <span className="font-medium">{t('rma.detail.receivedLabel', 'Received: ')}</span>
            {format(new Date(rma.receivedAt), 'MMM d, yyyy h:mm a')}
          </div>
        )}
        {rma.resolvedAt && (
          <div>
            <span className="font-medium">{t('rma.detail.resolvedLabel', 'Resolved: ')}</span>
            {format(new Date(rma.resolvedAt), 'MMM d, yyyy h:mm a')}
          </div>
        )}
      </div>
    </div>
  );
}
